import React, { type Node, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
  Banner,
  Box,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import usePropertyNaurCompliance from '../../hooks/data-fetching/usePropertyNaurCompliance';
import {
  useUnitNaurWarning,
  useNaurEventsForUnit,
} from '../../hooks/data-fetching/useNaurWarnings';
import messages from './messages';
import { hasOpenNaurEvents } from './utils';

// Variant Types
type AtomicVariant = 'property-generic' | 'unit-triggered' | 'unit-impacted';
type CompositeVariant = 'unit-smart';
type WarningVariant = AtomicVariant | CompositeVariant;

type Props = {
  variant: WarningVariant,
  propertyId: ?string,
  unitId?: ?string,
  sx?: Object,
};

// Shared Warning Banner Component
const Warning = ({ message, sx }: { message: Object, sx?: Object }): Node => (
  <Box sx={{ ...sx }}>
    <Banner status="warning" message={<FormattedMessage {...message} />} />
  </Box>
);

// ============================================================================
// ATOMIC VARIANTS (Single Responsibility Components)
// ============================================================================

/**
 * PropertyGenericWarning - Shows property-level NAUR warning
 *
 * Use case: Dashboard, Manage Unit Availability, Applicant Profile (no unit assigned)
 * Message: "There is at least one building at this property..."
 */
const PropertyGenericWarning = ({ sx }: { sx?: Object }): Node => (
  <Warning sx={sx} message={messages.propertyGenericMessage} />
);

/**
 * UnitTriggeredWarning - Shows if unit has triggered NAUR events
 * Use case: ViewUnit (Priority 1)
 * Message: "This unit has triggered a NAUR requirement..."
 * API Calls: 1 (useNaurEventsForUnit)
 */
const UnitTriggeredWarning = ({
  unitId,
  sx,
}: {
  unitId: ?string,
  sx?: Object,
}): Node => {
  const { data, isLoading, isError } = useNaurEventsForUnit(unitId);

  const hasOpenEvents = useMemo(() => hasOpenNaurEvents(data), [data]);

  if (isLoading || isError) return null;

  if (!hasOpenEvents) return null;

  return <Warning sx={sx} message={messages.naurTriggeredMessage} />;
};

/**
 * UnitImpactedWarning - Shows if unit is impacted by NAUR
 *
 * Use case: ApplicationProfile, NoticeToVacateModal, ViewUnit (Priority 2)
 * Message: "This unit is located in a building that is non-compliant..."
 * API Calls: 1 (useUnitNaurWarning)
 */
const UnitImpactedWarning = ({
  unitId,
  sx,
}: {
  unitId: ?string,
  sx?: Object,
}): Node => {
  const { data, isLoading, isError } = useUnitNaurWarning(unitId ?? null);

  if (isLoading || isError) return null;

  if (!data?.isImpactedByNaur) return null;

  return <Warning sx={sx} message={messages.naurWarningMessage} />;
};

// ============================================================================
// COMPOSITE VARIANTS (With Fallback Logic)
// ============================================================================

/**
 * UnitSmartWarning - Shows Priority 1 warning, falls back to Priority 2
 *
 * Use case: ViewUnit
 * Logic:
 *   - Priority 1: If unit has triggered NAUR events → show "triggered" message
 *   - Priority 2: If no triggered events but unit is impacted → show "impacted" message
 *   - Otherwise: Show nothing
 *
 * Performance: Uses sequential fetch - only fetches Priority 2 if Priority 1 shows nothing
 * API Calls:
 *   - If triggered events found: 1 (useNaurEventsForUnit only)
 *   - If no triggered events: 2 (useNaurEventsForUnit + useUnitNaurWarning)
 */
const UnitSmartWarning = ({
  unitId,
  sx,
}: {
  unitId: ?string,
  sx?: Object,
}): Node => {
  // Check Priority 1: Triggered events
  const {
    data: eventsData,
    isLoading: loadingEvents,
    isError: errorEvents,
  } = useNaurEventsForUnit(unitId);

  const hasOpenEvents = useMemo(
    () => hasOpenNaurEvents(eventsData),
    [eventsData],
  );

  // Wait for Priority 1 to finish loading
  if (loadingEvents) return null;

  // If Priority 1 found something (and no error), show it without checking Priority 2
  if (!errorEvents && hasOpenEvents) {
    return <Warning sx={sx} message={messages.naurTriggeredMessage} />;
  }

  // Priority 1 found nothing or errored, check Priority 2
  return <UnitImpactedWarning unitId={unitId} sx={sx} />;
};

// ============================================================================
// MAIN COMPONENT (Strategy Router)
// ============================================================================

/**
 * NaurWarning - Strategy Pattern Component
 *
 * Routes to appropriate warning component based on variant prop.
 * Always checks property compliance first - if property is compliant, shows nothing.
 *
 * Variants:
 *   Atomic:
 *     - property-generic: Shows generic property-level warning
 *     - unit-triggered: Shows if unit has triggered NAUR events
 *     - unit-impacted: Shows if unit is impacted by NAUR
 *
 *   Composite:
 *     - unit-smart: Priority 1 (triggered) → Priority 2 (impacted) fallback
 *
 * Usage:
 *   <NaurWarning variant="property-generic" propertyId={id} />
 *   <NaurWarning variant="unit-smart" propertyId={id} unitId={unitId} />
 */
const NaurWarning = ({ variant, propertyId, unitId, sx }: Props): Node => {
  // Always check property compliance first
  const { data: complianceData } = usePropertyNaurCompliance({
    propertyId,
    options: {
      enabled: Boolean(propertyId),
    },
  });

  const hasNonCompliantBuildings =
    complianceData?.hasNonCompliantBuildings || false;

  if (!hasNonCompliantBuildings) return null;

  switch (variant) {
    // ATOMIC VARIANTS
    case 'property-generic':
      return <PropertyGenericWarning sx={sx} />;

    case 'unit-triggered':
      if (!unitId) return null;
      return <UnitTriggeredWarning unitId={unitId} sx={sx} />;

    case 'unit-impacted':
      if (!unitId) return null;
      return <UnitImpactedWarning unitId={unitId} sx={sx} />;

    // COMPOSITE VARIANTS
    case 'unit-smart':
      if (!unitId) return null;
      return <UnitSmartWarning unitId={unitId} sx={sx} />;

    default:
      return null;
  }
};

export default NaurWarning;
